import './PacingDashboard.scss';
import {
  Dispatch,
  Fragment,
  ReactElement,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { Modal } from 'react-bootstrap';
import { MdAdd, MdClose, MdSort } from 'react-icons/md';
import { Button } from '@Components/Button/Button';
import { ColorKey, KeyShapeOptions } from '@Components/Charts/ChartTooltip/ChartTooltipV2';
import { DownloadDropdown } from '@Components/DownloadDropdown/DownloadDropdown';
import { EmptyState } from '@Components/EmptyState/EmptyState';
import { LoadingOverlay } from '@Components/LoadingOverlay/LoadingOverlay';
import { Dropdown } from '@Components/Dropdown/Dropdown';
import { DropdownToggleType } from '@Components/Dropdown/dropdown-constants';
import { IconButton } from '@Components/IconButton/IconButton';
import { Appearance, Placement, Size, StretchMode } from '@Components/designConstants';
import { FilterBar } from '@Components/FilterBar/FilterBar';
import { GoalPacingColors } from '@src/utils/colors';
import { downloadPNG, exportToExcel } from '@src/utils/download-utils';
import { measureTextWidth } from '@src/utils/canvasTextUtils';
import { Mixpanel, MxE } from '@src/utils/mixpanelWrapper';
import { PAGE_NAMES } from '@Components/Page/page-constants';
import { WidgetContainer } from '@Components/WidgetContainer/WidgetContainer';
import { PacingBarsDashboard } from './components/PacingBarsDashboard';
import { Simplicity } from './pacing-constants';
import {
  GOAL_VALUE_MAP,
  BarsFilterLine,
  barsTreeHasGoal,
  buildBarsFilterLines,
  filterBarsTree,
  goalNameToKey,
  isBarsSegment,
  pacingSortByOptions
} from './pacingUtils';

// Flatten the bars tree into one export row per segment node (plus the Total root), using the active
// goal + simple/adjusted mode.
const buildPacingExportRows = (
  node: Record<string, any> | undefined,
  goalKey: string,
  simplicity: string,
  path: string[] = []
): Record<string, string | number>[] => {
  if (!node || typeof node !== 'object') {
    return [];
  }
  const isSimple = simplicity === Simplicity.SIMPLE;
  const at = (key: string): number => node[key]?.[goalKey] ?? 0;
  const rows: Record<string, string | number>[] = [
    {
      Segment: path.join(' / ') || 'Total',
      Goal: at(isSimple ? 'booked' : 'adjustedBooked'),
      Actual: at(isSimple ? 'simpleDeliveryTotal' : 'adjustedDeliveryTotal'),
      Projected: at(isSimple ? 'simpleProjectedFuture' : 'adjustedProjectedFuture'),
      Remainder: at(isSimple ? 'simpleRemainderTotal' : 'adjustedRemainderTotal')
    }
  ];
  Object.entries(node).forEach(([key, value]) => {
    if (isBarsSegment(key, value)) {
      rows.push(...buildPacingExportRows(value, goalKey, simplicity, [...path, key]));
    }
  });
  return rows;
};

interface PacingDashboardProps {
  pacingBarsData: Record<string, any>;
  start: string;
  end: string;
  simplicity: string;
  setSimplicity: Dispatch<SetStateAction<string>>;
  isActual: boolean;
  showHelpModal: boolean;
  setShowHelpModal: Dispatch<SetStateAction<boolean>>;
  activeDimensions: string[];
  setActiveDimensions: Dispatch<SetStateAction<string[]>>;
  currentGoalValue: string;
  setCurrentGoalValue: Dispatch<SetStateAction<string>>;
  company: string;
  goalOptions: string[];
  segmentsPerGoal: Record<string, string[]>;
  isFetching: boolean;
  hasError: boolean;
}

export const PacingDashboard = ({
  pacingBarsData,
  start,
  end,
  simplicity,
  setSimplicity,
  isActual,
  showHelpModal,
  setShowHelpModal,
  activeDimensions,
  setActiveDimensions,
  currentGoalValue,
  setCurrentGoalValue,
  company,
  goalOptions,
  segmentsPerGoal,
  isFetching,
  hasError
}: PacingDashboardProps): ReactElement => {
  const goalKey = useMemo(() => goalNameToKey(currentGoalValue), [currentGoalValue]);
  const goalValueOptions = useMemo(
    () => goalOptions.map(g => ({ value: g, label: g })),
    [goalOptions]
  );

  const [longestText, setLongestText] = useState(0);
  const [sort, setSort] = useState('Abc');

  useEffect(() => {
    // Measure the widest segment-value label to size the Y-axis label column. The labels are the
    // bars tree's child-node keys (one per segment value at each level); collect them recursively.
    const labels = new Set<string>();
    const collect = (node: Record<string, any> | undefined) => {
      if (!node || typeof node !== 'object') {
        return;
      }
      Object.entries(node).forEach(([key, value]) => {
        if (isBarsSegment(key, value)) {
          labels.add(key);
          collect(value);
        }
      });
    };
    collect(pacingBarsData);
    // Measured at the y-axis labels' rendered weight (bodyBold), or the longest one overruns its column.
    const longest = Array.from(labels).reduce(
      (acc, value) => Math.max(acc, measureTextWidth(value, "600 1rem 'Geist'")),
      0
    );
    setLongestText(longest);
  }, [pacingBarsData]);

  const availableDimensions = useMemo(() => {
    const segments = segmentsPerGoal[currentGoalValue] || [];
    return segments.map(segment => ({ label: segment, value: segment }));
  }, [segmentsPerGoal, currentGoalValue]);

  const handleDimensionChange = (index: number, newDimension: string) => {
    const newDimensions = [...activeDimensions];
    newDimensions[index] = newDimension;
    setActiveDimensions(newDimensions);
  };

  const handleRemoveDimension = (index: number) => {
    const newDimensions = activeDimensions.filter((_, i) => i !== index);
    setActiveDimensions(newDimensions);
  };

  // Client-side FilterBar over the bars tree: each segment node becomes a line keyed by the active
  // dimensions, and the tree is pruned to the matching segments.
  // The filter belongs to the dimensions it was built for; a dimension change drops it and remounts
  // the FilterBar, which only re-emits on mount or token change.
  const dimensionsKey = activeDimensions.join('|');
  const [barsFilter, setBarsFilter] = useState<{
    dimensionsKey: string;
    filter: (line: BarsFilterLine) => boolean;
  }>();
  const activeBarsFilter =
    barsFilter?.dimensionsKey === dimensionsKey ? barsFilter.filter : undefined;
  const filterLines = useMemo(
    () => buildBarsFilterLines(pacingBarsData, activeDimensions),
    [pacingBarsData, activeDimensions]
  );
  const filteredBarsData = useMemo(
    () =>
      activeBarsFilter
        ? filterBarsTree(pacingBarsData, activeDimensions, activeBarsFilter)
        : pacingBarsData,
    [pacingBarsData, activeDimensions, activeBarsFilter]
  );

  // Bars legend swatches, coloured like the bars they key (see PacingBar's fill order).
  const legendItems = useMemo(() => {
    const [actualLabel, projectedLabel, remainderLabel] = GOAL_VALUE_MAP[currentGoalValue];
    const hasProjected = projectedLabel !== '';
    return [
      { label: actualLabel, color: GoalPacingColors.delivered },
      ...(hasProjected ? [{ label: projectedLabel, color: GoalPacingColors.projected }] : []),
      { label: remainderLabel, color: GoalPacingColors.remainder }
    ];
  }, [currentGoalValue]);

  const excelDownloadPacingData = useCallback(() => {
    exportToExcel(
      buildPacingExportRows(filteredBarsData, goalKey, simplicity),
      `pacing_${start}_${end}`
    );
    Mixpanel.track(MxE.ActionTriggered, { page: PAGE_NAMES.PACING, action: 'download bars excel' });
  }, [filteredBarsData, goalKey, simplicity, end, start]);

  const pngDownloadPacingData = useCallback(async () => {
    await downloadPNG('.bpm-page .body .cross-channel-pacing-dashboard', 'pacingBars');
    Mixpanel.track(MxE.ActionTriggered, { page: PAGE_NAMES.PACING, action: 'download bars png' });
  }, []);

  const filteredDimensions = useMemo(
    () => availableDimensions.filter(dimension => !activeDimensions.includes(dimension.value)),
    [availableDimensions, activeDimensions]
  );

  const hasGoalData = barsTreeHasGoal(pacingBarsData, goalKey);
  const bars =
    hasGoalData && barsTreeHasGoal(filteredBarsData, goalKey) ? (
      <PacingBarsDashboard
        pacingBarsData={filteredBarsData}
        activeDimensions={activeDimensions}
        simplicity={simplicity}
        setSimplicity={setSimplicity}
        longestText={longestText}
        goalName={currentGoalValue}
        start={start}
        end={end}
        isActual={isActual}
        currentGoalValue={currentGoalValue}
        sortOption={sort}
      />
    ) : null;

  const renderBarsBody = () => {
    // A refetch keeps the controls mounted and only covers the bars. The previous (placeholder)
    // tree may not carry the newly selected goal, so hold the empty state until the fetch settles.
    if (isFetching) {
      return <LoadingOverlay className='pacing-bars-loading'>{bars}</LoadingOverlay>;
    }
    if (bars) {
      return bars;
    }
    // A failed fetch already raised the error modal; don't also claim there's no data.
    if (hasError) {
      return null;
    }
    if (hasGoalData) {
      return (
        <EmptyState
          title='No segments match the current filter'
          description='Change or clear the filter to see more segments.'
        />
      );
    }
    return (
      <EmptyState
        title='No pacing data for this goal'
        description='Select a different goal or check the Goal Intake Tool.'
      >
        <a href={`/${company}/goal-intake`} target='_blank' rel='noopener noreferrer'>
          Goal Intake Tool
        </a>
      </EmptyState>
    );
  };

  return (
    <WidgetContainer
      className='cross-channel-pacing-dashboard'
      dataTestId='pacing-dashboard'
      header={
        <div className='pacing-dashboard-title'>
          Pacing&nbsp;to
          <Dropdown
            overlayPlacement={Placement.RIGHT}
            size={Size.LG}
            type={DropdownToggleType.WIDGET_TITLE}
            value={currentGoalValue}
            options={goalValueOptions}
            onChange={setCurrentGoalValue}
            disabled={isFetching}
          />
          {activeDimensions.map((dim, index) => (
            <Fragment key={`${index}-${dim}`}>
              {index === 0 ? 'by' : 'then\u00a0by'}
              <Dropdown
                overlayPlacement={Placement.RIGHT}
                size={Size.LG}
                type={DropdownToggleType.WIDGET_TITLE}
                value={dim}
                options={filteredDimensions}
                onChange={newDimension => handleDimensionChange(index, newDimension)}
                disabled={isFetching}
              />
              {index > 0 && (
                <IconButton
                  appearance={Appearance.EMPTY}
                  onClick={() => handleRemoveDimension(index)}
                >
                  <MdClose />
                </IconButton>
              )}
            </Fragment>
          ))}
          {activeDimensions.length > 0 && activeDimensions.length < 4 && (
            <Dropdown
              overlayPlacement={Placement.RIGHT}
              size={Size.LG}
              stretch={StretchMode.FIT}
              className='add-dimension-chip'
              value=''
              options={filteredDimensions}
              onChange={newDimension =>
                handleDimensionChange(activeDimensions.length, newDimension)
              }
              placeholder='Add Dimension'
              leadingIcon={<MdAdd />}
              disabled={isFetching}
            />
          )}
        </div>
      }
      rightActions={
        <div className='pacing-dashboard-buttons'>
          <Dropdown
            overlayPlacement={Placement.RIGHT}
            size={Size.LG}
            label='Sort by'
            value={sort}
            options={pacingSortByOptions}
            onChange={setSort}
            leadingIcon={<MdSort />}
          />
          <DownloadDropdown onClickOptions={[excelDownloadPacingData, pngDownloadPacingData]} />
        </div>
      }
    >
      <div className='pacing-dashboard-filters'>
        <FilterBar
          key={dimensionsKey}
          hasAdvanced
          onFilter={filter => setBarsFilter({ dimensionsKey, filter })}
          options={activeDimensions}
          lines={filterLines}
          useLocalStorage={false}
        />
        <div className='pacing-legend'>
          <div className='pacing-legend-head'>Legend:</div>
          {legendItems.map(({ label, color }) => (
            <div key={label} className='pacing-legend-item'>
              <ColorKey shape={KeyShapeOptions.SQUARE} color={color} />
              <div className='legend-label'>{label}</div>
            </div>
          ))}
          <div className='pacing-legend-item'>
            <div className='pacing-legend-budget-line' />
            <div className='legend-label legend-budget-text'>Budget</div>
          </div>
        </div>
      </div>

      {renderBarsBody()}

      {showHelpModal && (
        <Modal
          show
          size='lg'
          onHide={() => setShowHelpModal(false)}
          className='pacing-dashboard-modal'
        >
          <Modal.Header closeButton>
            <Modal.Title>What Do These Options Mean?</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              {`In general, "Simple" will figure out what the daily values for a flight should be, and compute booked and projected values from that. "Adjusted" will do various things such as taking overdelivery into account and focus more on what's left than what's already run.`}
            </p>
            <h2>Booked</h2>
            <p>
              {`In "Simple" mode, the amount booked will be the total booked for the flight, divided by the number of days in the flight, times the number of days in the date range. If the flight is from Monday through Sunday, and the date range is from Monday through Sunday, the booked amount will be exactly what's on the IO. If the flight is two weeks long, and the date range is only one week long, the amount booked will be half of what's on the IO. If the flight ends before the date range ends, it will not multiply by the full date range but only up to when the flight ends, so if the date range is two weeks, and the flight is one week, the booked will be the total booked on the IO.`}
            </p>
            <p>
              {`In "Adjusted" mode, it will first deduct any impressions served for that flight before the selected date range. It will then divide the remaining booked impressions by the number of days remaining in the flight, then multiply by the length of the date range. If the flight is contained entirely within the date range, this number should be the same as the simple version.`}
            </p>
            <p>
              Suppose there was a flight that was 10 days long and had a spend of $1,000. For 5 days
              it paced perfectly ($100 a day) and delivered a total of $500. Then we double the
              spend, making it $2,000. If our date range is the full ten days, both the Simple and
              Adjusted booked numbers will be $2,000. However, suppose the date range only covered
              the final 5 days. In Simple mode it would be the full flight spend ($2,000) divided by
              the number of days in the flight (10), times the number of days in our range (5),
              which would be $1,000 (or half the total spend). In Adjusted mode, it would be the
              full flight spend ($2,000) minus what was delivered outside of the date range ($500,
              for a remaining total of $1,500), divided by the remaining days in the flight (5),
              times the days in our date range (5), for a total of $1,500.
            </p>
            <h2>Projected</h2>
            <p>
              {`In "Simple" mode, we take the number of impressions served in the date range, divide by the number of days so far, then multiplied by the full date range. In "Adjusted" mode, we take that number, but cut it off at the booked amount, assuming that once the network hits the IO amount, they'll stop delivering.`}
            </p>
            <h2>Actual Delivery</h2>
            <p>
              {`The "Simple" and "Adjusted" delivered impressions will always be the same, because these are the number of impressions we got. The "Simple" spend will be that number times the cpm divided by 1000. The "Adjusted" spend will be that number, but with overdelivery taken into account.`}
            </p>
            <h2>Ideal Delivery</h2>
            <p>
              {`"Ideal Delivery" refers to the "Ideal Daily Delivery" and "Ideal Delivery" lines in the charts that appear when selecting an item in the right pane. In "Simple" mode, the ideal daily delivery will be the total spend of the flight divided by the number of days in the flight. The Ideal total delivery will be that number times the number of days. In other words, the ideal daily delivery line will be flat, and the ideal delivery line will be a straight increasing line. In "Adjusted" mode, instead of looking at what the pacing should be for the whole flight, it looks at what the pacing should be based on the remaining impressions. For each day it takes the number of remaining impressions, then divides by the number of remaining days. Thus, if a flight is under-delivering, the ideal line will get higher, and if it's over-delivering, it will get lower.`}
            </p>
            <p>
              {`Consider the above example with the 10 day flight at $1,000 that doubles in spend half way through. Looking at the last five days, "Simple" mode will compute the ideal daily spend as the total spend ($2,000) divided by the total days in the flight (10), to get the ideal daily spend ($200). So it will show a horizontal line at $200 and a straight line that starts at $0 with a slope of $200 per day. In "Adjusted" mode, we'll take the full booked amount ($2,000), subtract the already delivered impressions ($500, for a new total of $1,500), then divide that by the total number of remaining days (5) to get an ideal daily spend of $300. If we were to look at it for the entire 10 day range, on day one the ideal daily spend will be $200. After the first day (where we deliver $100) the remaining spend will be $1,900. With 9 days left the ideal daily spend will be $211. After we deliver another $100 the total will be $1,800, and with 8 days left that makes the ideal daily spend $225. Every day the ideal spend increases because we're under-delivering when looking at the entire range. Once we get to day 5, the ideal daily spend will be $300. If they deliver $300 a day then the ideal will stay at $300 a day. If on the first day they deliver $400, then the new total will be $2,000 - $500 - $400 = $1,100, which divided by the remaining days (4) is $275.`}
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button onClick={() => setShowHelpModal(false)}>Got It</Button>
          </Modal.Footer>
        </Modal>
      )}
    </WidgetContainer>
  );
};
